import { FileType } from 'src/components/10-atoms/file-picker/types';
import { getFieldState } from 'src/components/10-atoms/form/hooks/useFieldState/utils';
import useFormState from 'src/components/10-atoms/form/hooks/useFormState';
import { getFileType } from 'src/components/10-atoms/form/inputs/FileInput/utils';
import { snackbarHelperActions } from 'src/components/20-helper-views/snackbar/useSnackbar';
import useStableCallback from 'src/hooks/useCallbackUtils/useStableCallback';
import useEffectUntilDone from 'src/hooks/useEffectUtils/useEffectUntilDone';
import useContributionResponseHandler from 'src/modules/exam-preparation/containers/ContributionResponseHandler/useContributionResponseHandler';
import { useQPDetailsQuery } from 'src/modules/exam-preparation/graphql/qp/getDetails';
import { useEPUpdateQPMutation } from 'src/modules/exam-preparation/graphql/qp/update';
import { useEPUploadQPMutation } from 'src/modules/exam-preparation/graphql/qp/upload';
import { useSubjectDetailsQuery } from 'src/modules/exam-preparation/graphql/subject/getDetails';
import { stripTimeZone } from 'src/utils/date';
function qpImageToFileInputValue(image) {
    return {
        id: image.uri,
        file: {
            uri: image.uri,
            filename: image.uri.split('/').pop() ?? 'image',
            type: getFileType(image.uri) === FileType.PDF ? 'application/pdf' : 'image/*',
        },
        uploadRequest: {
            loading: false,
            error: null,
            data: { publicURL: image.uri },
        },
    };
}
export default function useData({ subjectID, qpID }) {
    const isEditMode = !!qpID;
    const [subjectDetailsQueryState] = useSubjectDetailsQuery(subjectID ?? '', {
        skip: !subjectID,
    });
    const [qpDetailsQueryState] = useQPDetailsQuery(qpID ?? '', {
        skip: !isEditMode,
    });
    const [uploadMutationState, uploadMutationActions] = useEPUploadQPMutation();
    const [updateMutationState, updateMutationActions] = useEPUpdateQPMutation();
    const [formState, formActions] = useFormState({
        fields: {
            date: getFieldState(null),
            label: getFieldState(''),
            qpImages: getFieldState([]),
        },
    }, {
        fieldValidations: {
            label({ value }) {
                if (!isEditMode) {
                    return true;
                }
                if (!value) {
                    return 'Please enter a label';
                }
                if (value.length > 20) {
                    return 'Label cannot be more than 20 characters';
                }
                return true;
            },
            date({ value }) {
                if (!value) {
                    return 'Please enter a valid examination date';
                }
                return true;
            },
            qpImages({ value }) {
                if (value.length === 0) {
                    return 'Please upload Question Paper images';
                }
                if (value.length > 20) {
                    return 'You can only include upto 20 images';
                }
                return true;
            },
        },
    });
    // Seed the form with existing QP details once, in edit mode
    useEffectUntilDone((done) => {
        const qp = qpDetailsQueryState.data?.ep_qp;
        if (!isEditMode || !qp) {
            return;
        }
        formActions.fields.date.setValue(new Date(qp.data.dateNL));
        formActions.fields.label.setValue(qp.data.label ?? '');
        formActions.fields.qpImages.setValue((qp.data.qpImages ?? []).map(qpImageToFileInputValue));
        done();
    }, [qpDetailsQueryState.data]);
    const handleSubmit = useStableCallback(() => {
        if (!formActions.validateForm()) {
            return;
        }
        const qpUploading = formState.fields.qpImages.value.some(qpImage => qpImage.uploadRequest.loading);
        if (qpUploading) {
            snackbarHelperActions.show({
                message: 'Please wait until the images are uploaded',
                duration: 'SHORT',
            });
            return;
        }
        const qpImages = formState.fields.qpImages.value
            .filter(qpImage => !!qpImage.uploadRequest.data)
            .map(qpImage => ({
            uri: qpImage.uploadRequest.data.publicURL,
        }));
        if (isEditMode) {
            updateMutationActions.submit({
                variables: {
                    data: {
                        id: qpID,
                        dateNL: stripTimeZone(formState.fields.date.value),
                        label: formState.fields.label.value || undefined,
                        qpImages,
                    },
                },
            });
            return;
        }
        uploadMutationActions.submit({
            variables: {
                data: {
                    dateNL: stripTimeZone(formState.fields.date.value),
                    subjectID: subjectID,
                    qpImages,
                },
            },
        });
    });
    const [, contributionResponseHandler] = useContributionResponseHandler({
        sentForReviewContent: {
            title: 'Question paper uploaded successfully 🎉',
            howItWorksSteps: [
                'MarineSamraj team will review & digitalize the question paper',
                'Once Approved, the question paper will be added',
                "You'll be notified about the updates",
            ],
        },
        thankContributorContent: {
            title: isEditMode
                ? 'Question Paper updated successfully 🎉'
                : 'QP Added Successfully 🎉',
        },
    });
    const contributionResponse = isEditMode
        ? updateMutationState.data?.ep_updateQP
        : uploadMutationState.data?.ep_uploadQP;
    const submitting = isEditMode
        ? updateMutationState.loading
        : uploadMutationState.loading;
    const state = {
        isEditMode,
        form: formState,
        subjectDetailsQuery: subjectDetailsQueryState,
        submitting,
        contributionResponse,
    };
    const actions = {
        form: formActions,
        handleSubmit,
        contributionResponseHandler,
    };
    return [state, actions];
}
